'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { ColumnSelector } from '../ColumnSelector';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { Plus, Trash } from '@phosphor-icons/react';

interface Condition {
  column: string;
  operator: string;
  value: string;
  logic: 'AND' | 'OR';
}

interface FilterConfigProps {
  nodeId: string;
  config: {
    conditions?: Condition[];
  };
}

const operators = [
  { value: '==', label: '等于', types: ['string', 'number', 'date', 'boolean'] },
  { value: '!=', label: '不等于', types: ['string', 'number', 'date', 'boolean'] },
  { value: '>', label: '大于', types: ['number', 'date'] },
  { value: '<', label: '小于', types: ['number', 'date'] },
  { value: '>=', label: '大于等于', types: ['number', 'date'] },
  { value: '<=', label: '小于等于', types: ['number', 'date'] },
  { value: 'contains', label: '包含', types: ['string'] },
  { value: 'starts_with', label: '开头是', types: ['string'] },
  { value: 'ends_with', label: '结尾是', types: ['string'] },
  { value: 'is_null', label: '为空', types: ['string', 'number', 'date', 'boolean'] },
  { value: 'is_not_null', label: '不为空', types: ['string', 'number', 'date', 'boolean'] },
];

export function FilterConfig({ nodeId, config }: FilterConfigProps) {
  const { updateNodeConfig } = useWorkflowStore();
  const [conditions, setConditions] = useState<Condition[]>(
    config.conditions || [{ column: '', operator: '==', value: '', logic: 'AND' }]
  );
  const inputColumns = useNodeInputColumns(nodeId);

  const updateCondition = (index: number, updates: Partial<Condition>) => {
    const newConditions = conditions.map((c, i) =>
      i === index ? { ...c, ...updates } : c
    );
    setConditions(newConditions);
    updateNodeConfig(nodeId, {
      type: 'filter',
      params: { conditions: newConditions },
    });
  };

  const addCondition = () => {
    const newConditions: Condition[] = [
      ...conditions,
      { column: '', operator: '==', value: '', logic: 'AND' },
    ];
    setConditions(newConditions);
    updateNodeConfig(nodeId, {
      type: 'filter',
      params: { conditions: newConditions },
    });
  };

  const removeCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    setConditions(newConditions);
    updateNodeConfig(nodeId, {
      type: 'filter',
      params: { conditions: newConditions },
    });
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-zinc-700">筛选条件</div>
      
      <AnimatePresence>
        {conditions.map((condition, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="p-4 bg-zinc-50 rounded-xl border border-zinc-100 space-y-3"
          >
            {index > 0 && (
              <div className="flex justify-end">
                <select
                  value={condition.logic}
                  onChange={(e) =>
                    updateCondition(index, { logic: e.target.value as 'AND' | 'OR' })
                  }
                  className="text-xs font-medium bg-white border border-zinc-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                >
                  <option value="AND">且 (AND)</option>
                  <option value="OR">或 (OR)</option>
                </select>
              </div>
            )}
            
            <div className="space-y-3">
              {/* Column Selector */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-500">选择列</label>
                <ColumnSelector
                  value={condition.column}
                  onChange={(columnName) => updateCondition(index, { column: columnName })}
                  placeholder={inputColumns ? '选择要筛选的列...' : '请先连接输入节点'}
                  columns={inputColumns}
                />
              </div>
              
              {/* Operator */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-500">条件</label>
                <select
                  value={condition.operator}
                  onChange={(e) => updateCondition(index, { operator: e.target.value })}
                  className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                >
                  {operators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Value */}
              {!['is_null', 'is_not_null'].includes(condition.operator) && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-500">值</label>
                  <input
                    type="text"
                    placeholder="输入筛选值"
                    value={condition.value}
                    onChange={(e) => updateCondition(index, { value: e.target.value })}
                    className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
                  />
                </div>
              )}
            </div>
            
            {conditions.length > 1 && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => removeCondition(index)}
                className="flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
              >
                <Trash className="w-3.5 h-3.5" />
                删除条件
              </motion.button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
      
      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={addCondition}
        className="flex items-center gap-2 w-full justify-center px-4 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors"
      >
        <Plus className="w-4 h-4" />
        添加条件
      </motion.button>
    </div>
  );
}
